import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  GamecardCategoryState,
  GamecardCategoryResponse,
  GamecardCategory,
} from "./types/game-types";
import { PaginationReturn } from "./types/types";

const initialState: GamecardCategoryState = {
  gamecardCategories: [],
  gamecardCategory: null,
  selectedGamecardCategory: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE GamecardCategory (supports FormData for image upload)
export const createGamecardCategory = createAsyncThunk<
  GamecardCategoryResponse,
  FormData | Omit<GamecardCategory, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("gamecardCategories/create", async (gamecardCategoryData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = gamecardCategoryData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/game-cards-categories`,
      gamecardCategoryData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return {
      message: res.data.message,
      dataCategory: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data category" }
    );
  }
});

// READ (fetch all) GamecardCategories (with pagination)
export const fetchGamecardCategories = createAsyncThunk<
  GamecardCategoryResponse & PaginationReturn,
  void | { url: string },
  { rejectValue: ApiError; state: RootState }
>("gamecardCategories/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/game-cards-categories`;
    const res = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      gamecardCategories: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card categories" }
    );
  }
});

// READ (fetch one) DataCategory by ID
export const fetchGamecardCategoryById = createAsyncThunk<
  GamecardCategory,
  number,
  { rejectValue: ApiError; state: RootState }
>("gamecardCategories/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/game-cards-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch game card category" }
    );
  }
});

// UPDATE DataCategory (supports FormData for image upload)
export const updateGamecardCategory = createAsyncThunk<
  GamecardCategory,
  { id: number; data: Partial<GamecardCategory> | FormData },
  { rejectValue: ApiError; state: RootState }
>("gamecardCategories/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/game-cards-categories/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update game card category" }
    );
  }
});

// DELETE DataCategory
export const deleteGamecardCategory = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("gamecardCategories/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/game-cards-categories/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete game card category" }
    );
  }
});

// Slice
const gamecardCategorySlice = createSlice({
  name: "gamecardCategories",
  initialState,
  reducers: {
    resetGamecardCategoryState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedGamecardCategory = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createGamecardCategory.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createGamecardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.gamecardCategory) {
          state.gamecardCategories.unshift(action.payload.gamecardCategory);
        }
      })
      .addCase(createGamecardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create game card category";
      })

      // Fetch All
      .addCase(fetchGamecardCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGamecardCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.gamecardCategories = action.payload.gamecardCategories || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchGamecardCategories.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch game card categories";
      })

      // Fetch One
      .addCase(fetchGamecardCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedGamecardCategory = null;
      })
      .addCase(fetchGamecardCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedGamecardCategory = action.payload;
      })
      .addCase(fetchGamecardCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch game card category";
      })

      // Update
      .addCase(updateGamecardCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateGamecardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.gamecardCategories = state.gamecardCategories.map((category) =>
          category.id === action.payload.id ? action.payload : category
        );
        if (state.selectedGamecardCategory?.id === action.payload.id) {
          state.selectedGamecardCategory = action.payload;
        }
      })
      .addCase(updateGamecardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update game card category";
      })

      // Delete
      .addCase(deleteGamecardCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteGamecardCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.gamecardCategories = state.gamecardCategories.filter(
          (category) => category.id !== action.payload
        );
      })
      .addCase(deleteGamecardCategory.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete game card category";
      });
  },
});

export const { resetGamecardCategoryState } = gamecardCategorySlice.actions;
export default gamecardCategorySlice.reducer;
